import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw, Search, Filter, ExternalLink, Copy, CheckCircle,
  AlertTriangle, Clock, Star, Loader2, Send, ChevronLeft, ChevronRight,
  Languages, ChevronDown, ChevronUp, Ban, RotateCcw,
} from 'lucide-react';
import {
  fetchArticles, triggerFetch, triggerReport, triggerFetchAndReport, toggleDuplicate,
  fetchTranslationStatus, requestSummary, requestFullTranslation, fetchTranslation,
} from '../api';

const HOUR_OPTIONS = [
  { value: 3, label: '최근 3시간' },
  { value: 6, label: '최근 6시간' },
  { value: 12, label: '최근 12시간' },
  { value: 24, label: '최근 24시간' },
  { value: 48, label: '최근 48시간' },
];

// 수집 스케줄: 9~18시 30분 간격 (매일)
const FETCH_SCHEDULE_MINUTES = [
  9*60, 9*60+30,
  10*60, 10*60+30,
  11*60, 11*60+30,
  12*60, 12*60+30,
  13*60, 13*60+30,
  14*60, 14*60+30,
  15*60, 15*60+30,
  16*60, 16*60+30,
  17*60, 18*60,
];

// 슬롯 허용 오차: ±40분 (수집이 약간 늦게 완료돼도 포함)
const SLOT_MARGIN_MS = 40 * 60 * 1000;

function getRecentFetchSlots(count = 8) {
  const now = new Date();
  const todayMins = now.getHours() * 60 + now.getMinutes();
  const slots = [];

  // 오늘 이미 지난 슬롯
  const todayPast = FETCH_SCHEDULE_MINUTES.filter((m) => m <= todayMins).reverse();
  for (const m of todayPast) {
    const d = new Date(now);
    d.setHours(Math.floor(m / 60), m % 60, 0, 0);
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    const diffH = (now - d) / 3600000;
    slots.push({ label: `오늘 ${hh}:${mm}`, since: new Date(d.getTime() - SLOT_MARGIN_MS).toISOString(), hours: diffH });
    if (slots.length >= count) break;
  }

  // 어제 슬롯으로 채우기
  if (slots.length < count) {
    const yesterday = [...FETCH_SCHEDULE_MINUTES].reverse();
    for (const m of yesterday) {
      const d = new Date(now);
      d.setDate(d.getDate() - 1);
      d.setHours(Math.floor(m / 60), m % 60, 0, 0);
      const hh = String(Math.floor(m / 60)).padStart(2, '0');
      const mm = String(m % 60).padStart(2, '0');
      const diffH = (now - d) / 3600000;
      slots.push({ label: `어제 ${hh}:${mm}`, since: new Date(d.getTime() - SLOT_MARGIN_MS).toISOString(), hours: diffH });
      if (slots.length >= count) break;
    }
  }

  return slots;
}

function formatAbsTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const hhmm = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Seoul' });
  if (isToday) return hhmm;
  if (isYesterday) return `어제 ${hhmm}`;
  const mmdd = `${d.getMonth() + 1}/${d.getDate()}`;
  return `${mmdd} ${hhmm}`;
}

const TYPE_BADGE = {
  domestic: { label: '국내', color: '#0c4a6e', text: '#38bdf8' },
  overseas: { label: '해외(국문)', color: '#134e4a', text: '#2dd4bf' },
  column:   { label: '칼럼', color: '#3b0764', text: '#c084fc' },
  foreign:  { label: '영문', color: '#422006', text: '#fb923c' },
};

function TranslationPanel({ article, aiEnabled }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);

  const { data: cached, refetch } = useQuery({
    queryKey: ['translation', article.id],
    queryFn: () => fetchTranslation(article.id),
    enabled: open,
    staleTime: Infinity,
  });

  const summaryMut = useMutation({
    mutationFn: () => requestSummary(article.id),
    onSuccess: () => { setMode('summary'); refetch(); },
  });
  const fullMut = useMutation({
    mutationFn: () => requestFullTranslation(article.id),
    onSuccess: () => { setMode('full'); refetch(); },
  });

  if (article.lang !== 'en') return null;

  return (
    <div className="translation-panel">
      <button className="trans-toggle" onClick={() => setOpen((v) => !v)}>
        <Languages size={12} />
        {open ? '번역 닫기' : (cached?.summary_ko ? 'AI 요약 보기' : 'AI 번역')}
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {open && (
        <div className="trans-content">
          {!aiEnabled ? (
            <div className="trans-notice">
              🔑 Claude API 키 설정 후 번역 기능이 활성화됩니다.
            </div>
          ) : (
            <>
              <div className="trans-actions">
                <button
                  className={`trans-btn ${mode === 'summary' || cached?.summary_ko ? 'active' : ''}`}
                  onClick={() => { setMode('summary'); if (!cached?.summary_ko) summaryMut.mutate(); }}
                  disabled={summaryMut.isPending}
                >
                  {summaryMut.isPending ? <Loader2 size={11} className="spin" /> : null}
                  {cached?.summary_ko ? '✓ AI 요약' : 'AI 요약 생성'}
                </button>
                <button
                  className={`trans-btn ${mode === 'full' ? 'active' : ''}`}
                  onClick={() => { setMode('full'); if (!cached?.full_translation) fullMut.mutate(); }}
                  disabled={fullMut.isPending}
                >
                  {fullMut.isPending ? <Loader2 size={11} className="spin" /> : null}
                  {cached?.full_translation ? '✓ 상세 번역' : '상세 번역 요청'}
                </button>
                {cached?.translated_at && (
                  <span className="trans-meta">
                    캐시됨 · {new Date(cached.translated_at).toLocaleDateString('ko-KR')}
                  </span>
                )}
              </div>

              {mode === 'summary' && cached?.summary_ko && (
                <div className="trans-text">{cached.summary_ko}</div>
              )}
              {mode === 'full' && cached?.full_translation && (
                <div className="trans-text">{cached.full_translation}</div>
              )}
              {(summaryMut.isError || fullMut.isError) && (
                <div className="trans-error">
                  {(summaryMut.error || fullMut.error)?.response?.data?.error || '번역 오류'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ArticleRow({ article, isAdmin, onToggleDuplicate, aiEnabled }) {
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState(false);
  const typeBadge = TYPE_BADGE[article.article_type] || TYPE_BADGE.domestic;

  async function handleToggle() {
    setPending(true);
    await onToggleDuplicate(article.id, !article.is_duplicate);
    setPending(false);
  }

  return (
    <div className={`article-row ${article.is_duplicate ? 'duplicate' : ''}`}>
      <div className="article-main">
        <div className="article-meta">
          <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: '3px',
            background: typeBadge.color, color: typeBadge.text }}>{typeBadge.label}</span>
          <span className="badge badge-blue" style={{ fontSize: '0.65rem' }}>{article.source}</span>
          <span className="article-time">
            <Clock size={11} style={{ display: 'inline', marginRight: 3 }} />
            {formatAbsTime(article.published_at || article.collected_at)}
          </span>
          {article.is_personnel === 1 && <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', background: '#451a03', color: '#fb923c' }}>🔔 인사</span>}
          {article.sent_to_telegram === 1 && <span className="badge badge-green" style={{ fontSize: '0.65rem' }}>텔레그램</span>}
          {article.is_duplicate === 1 && <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>중복</span>}
          <span style={{ fontSize: '0.65rem', color: '#475569', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            ★ {article.relevance_score?.toFixed(1)}
            {isAdmin && <span style={{ color: '#334155', fontSize: '0.6rem' }}>#{article.id}</span>}
          </span>
        </div>
        <a href={article.link} target="_blank" rel="noopener noreferrer"
          className={`article-title ${article.is_duplicate ? 'dim' : ''}`}>
          {article.title}
          <ExternalLink size={12} className="ext-icon" />
        </a>
        <TranslationPanel article={article} aiEnabled={aiEnabled} />
      </div>
      <div className="article-actions">
        <button className="icon-btn" onClick={() => { navigator.clipboard.writeText(article.link); setCopied(true); setTimeout(() => setCopied(false), 1500); }} title="링크 복사">
          {copied ? <CheckCircle size={14} color="#4ade80" /> : <Copy size={14} />}
        </button>
        {isAdmin && (
          <button
            className={`icon-btn ${article.is_duplicate ? 'dup-restore' : 'dup-mark'}`}
            onClick={handleToggle}
            disabled={pending}
            title={article.is_duplicate ? '중복 해제 (목록에 다시 표시)' : '🚫 중복으로 처리 (이 기사 숨김 + 패턴 학습)'}
          >
            {pending ? <Loader2 size={13} className="spin" /> : article.is_duplicate ? <RotateCcw size={13} /> : <Ban size={13} />}
          </button>
        )}
      </div>
    </div>
  );
}

function SectionBlock({ title, icon, articles, isAdmin, onToggleDuplicate, aiEnabled, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!articles || articles.length === 0) return null;
  return (
    <div className="section-block">
      <button className="section-header" onClick={() => setOpen((v) => !v)}>
        <span>{icon} <strong>{title}</strong> <span className="section-count">{articles.length}건</span></span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && articles.map((a) => (
        <ArticleRow key={a.id} article={a} isAdmin={isAdmin} onToggleDuplicate={onToggleDuplicate} aiEnabled={aiEnabled} />
      ))}
    </div>
  );
}

export default function NewsPage({ isAdmin }) {
  const queryClient = useQueryClient();
  const [hours, setHours] = useState(6);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [excludeDuplicates, setExcludeDuplicates] = useState(true);
  const fetchSlots = getRecentFetchSlots(8);
  const [filterMode, setFilterMode] = useState('slot');
  const [selectedSlot, setSelectedSlot] = useState(() => fetchSlots[0] || null);

  const effectiveHours = filterMode === 'slot' && selectedSlot ? selectedSlot.hours + 0.5 : hours;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['articles', { hours: effectiveHours, search, page, excludeDuplicates }],
    queryFn: () => fetchArticles({ hours: Math.ceil(effectiveHours), search, page, limit: 100, excludeDuplicates }),
    keepPreviousData: true,
  });

  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: fetchTranslationStatus,
    staleTime: 60000,
  });
  const aiEnabled = aiStatus?.enabled === true;

  const fetchMutation = useMutation({
    mutationFn: triggerFetch,
    onSuccess: () => queryClient.invalidateQueries(['articles']),
  });
  const reportMutation = useMutation({ mutationFn: () => triggerReport('today') });
  const fetchAndReportMutation = useMutation({
    mutationFn: triggerFetchAndReport,
    onSuccess: () => queryClient.invalidateQueries(['articles']),
  });
  const dupMutation = useMutation({
    mutationFn: ({ id, val }) => toggleDuplicate(id, val),
    onSuccess: () => queryClient.invalidateQueries(['articles']),
  });

  const handleSearch = useCallback((e) => {
    e.preventDefault(); setSearch(searchInput); setPage(1);
  }, [searchInput]);

  const slotCutoff = filterMode === 'slot' && selectedSlot ? new Date(selectedSlot.since) : null;

  const allArticles = data?.articles || [];
  const articles = slotCutoff
    ? allArticles.filter((a) => new Date(a.collected_at) >= slotCutoff)
    : allArticles;
  const total = slotCutoff ? articles.length : (data?.total || 0);
  const pages = data?.pages || 1;

  const domestic = articles.filter((a) => a.lang === 'ko' && a.article_type === 'domestic');
  const column   = articles.filter((a) => a.article_type === 'column');
  const overseas = articles.filter((a) => a.lang === 'ko' && a.article_type === 'overseas');
  const foreign  = articles.filter((a) => a.lang === 'en'  || a.article_type === 'foreign');
  const hasSearch = !!search;

  const handleDup = (id, val) => dupMutation.mutate({ id, val });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <Star size={20} color="#facc15" />
          에너지·원자력 뉴스
          {(isLoading || isFetching) && <span className="spinner" style={{ width: 16, height: 16, marginLeft: 8 }} />}
        </h1>
        <div className="actions-row">
          {!aiEnabled && (
            <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <Languages size={12} /> 번역 비활성
            </span>
          )}
          {isAdmin && (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => fetchMutation.mutate()} disabled={fetchMutation.isPending || fetchAndReportMutation.isPending}>
                {fetchMutation.isPending ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} 지금 수집
              </button>
              <button className="btn btn-success btn-sm" onClick={() => reportMutation.mutate()} disabled={reportMutation.isPending || fetchAndReportMutation.isPending}>
                <Send size={14} /> 리포트 전송
              </button>
              <button
                className="btn btn-sm"
                style={{ background: '#7c3aed', color: '#fff' }}
                onClick={() => fetchAndReportMutation.mutate()}
                disabled={fetchAndReportMutation.isPending || fetchMutation.isPending}
                title="수집 후 즉시 텔레그램 전송"
              >
                {fetchAndReportMutation.isPending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                지금 수집 &amp; 전송
              </button>
            </>
          )}
        </div>
      </div>

      <div className="filters card" style={{ marginBottom: '1rem' }}>
        <div className="filters-inner">
          <form onSubmit={handleSearch} className="search-form">
            <input className="input" placeholder="키워드 검색..." value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)} style={{ minWidth: '180px' }} />
            <button className="btn btn-secondary btn-sm" type="submit"><Search size={14} /></button>
          </form>
          <div className="filter-mode-group">
            <div className="filter-mode-tabs">
              <button
                className={`filter-mode-tab ${filterMode === 'hours' ? 'active' : ''}`}
                onClick={() => { setFilterMode('hours'); setPage(1); }}
              >시간 범위</button>
              <button
                className={`filter-mode-tab ${filterMode === 'slot' ? 'active' : ''}`}
                onClick={() => { setFilterMode('slot'); setPage(1); }}
              >수집 회차</button>
            </div>
            {filterMode === 'hours' ? (
              <select className="select" value={hours} onChange={(e) => { setHours(Number(e.target.value)); setPage(1); }}>
                {HOUR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <select className="select" value={selectedSlot?.since || ''}
                onChange={(e) => {
                  const s = fetchSlots.find((sl) => sl.since === e.target.value);
                  setSelectedSlot(s); setPage(1);
                }}>
                {fetchSlots.map((s) => (
                  <option key={s.since} value={s.since}>{s.label} 수집분</option>
                ))}
              </select>
            )}
          </div>
          <label className="toggle-label">
            <input type="checkbox" checked={excludeDuplicates} onChange={(e) => { setExcludeDuplicates(e.target.checked); setPage(1); }} />
            중복 제외
          </label>
          <span className="result-count"><Filter size={13} /> 총 <strong>{total}</strong>건</span>
        </div>
      </div>

      {fetchMutation.isSuccess && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>✅ 수집 완료! {fetchMutation.data?.count || 0}건 처리됨</div>
      )}
      {reportMutation.isSuccess && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>✅ 텔레그램 리포트 전송 완료</div>
      )}

      {isLoading ? (
        <div className="card empty-state"><div className="spinner" /><span>뉴스 불러오는 중...</span></div>
      ) : articles.length === 0 ? (
        <div className="card empty-state"><AlertTriangle size={32} color="#475569" /><span>해당 시간대에 수집된 뉴스가 없습니다.</span></div>
      ) : hasSearch ? (
        <div className="card">
          {articles.map((a) => <ArticleRow key={a.id} article={a} isAdmin={isAdmin} onToggleDuplicate={handleDup} aiEnabled={aiEnabled} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="card">
            <SectionBlock title="국내 주요 뉴스" icon="🇰🇷" articles={domestic} isAdmin={isAdmin} onToggleDuplicate={handleDup} aiEnabled={aiEnabled} />
            <SectionBlock title="칼럼·분석" icon="📝" articles={column} isAdmin={isAdmin} onToggleDuplicate={handleDup} aiEnabled={aiEnabled} defaultOpen={false} />
            <SectionBlock title="해외 소식 (국문)" icon="🌏" articles={overseas} isAdmin={isAdmin} onToggleDuplicate={handleDup} aiEnabled={aiEnabled} defaultOpen={false} />
            <SectionBlock title="영문 뉴스" icon="🌐" articles={foreign} isAdmin={isAdmin} onToggleDuplicate={handleDup} aiEnabled={aiEnabled} defaultOpen={false} />
          </div>
        </div>
      )}

      {pages > 1 && hasSearch && (
        <div className="pagination">
          <button className="btn btn-secondary btn-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft size={14} /></button>
          <span className="page-info">{page} / {pages}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}><ChevronRight size={14} /></button>
        </div>
      )}

      <style>{`
        .filters-inner { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
        .search-form { display: flex; gap: 0.4rem; }
        .filter-mode-group { display: flex; align-items: center; gap: 0.25rem; }
        .filter-mode-tabs { display: flex; border-radius: 6px; overflow: hidden; border: 1px solid #334155; }
        .filter-mode-tab { padding: 0.3rem 0.65rem; border: none; background: #1e293b; color: #64748b; font-size: 0.78rem; cursor: pointer; transition: all 0.12s; }
        .filter-mode-tab:hover { color: #94a3b8; }
        .filter-mode-tab.active { background: #334155; color: #e2e8f0; font-weight: 600; }
        .toggle-label { display: flex; align-items: center; gap: 0.4rem; font-size: 0.875rem; color: #94a3b8; cursor: pointer; }
        .result-count { font-size: 0.8rem; color: #64748b; display: flex; align-items: center; gap: 0.3rem; }
        .actions-row { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

        .section-block { margin-bottom: 0.25rem; }
        .section-header {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          background: #0f172a; border: none; border-radius: 6px;
          padding: 0.5rem 0.75rem; color: #94a3b8; font-size: 0.875rem; cursor: pointer;
          margin-bottom: 0.25rem; transition: background 0.12s;
        }
        .section-header:hover { background: #1e293b; color: #e2e8f0; }
        .section-count { font-size: 0.75rem; color: #475569; margin-left: 0.4rem; font-weight: 400; }

        .article-row { display: flex; align-items: flex-start; justify-content: space-between; padding: 0.75rem 0.5rem; border-bottom: 1px solid #1e293b; gap: 0.75rem; }
        .article-row:last-child { border-bottom: none; }
        .article-row.duplicate { opacity: 0.45; }
        .article-main { flex: 1; display: flex; flex-direction: column; gap: 0.3rem; min-width: 0; }
        .article-meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }
        .article-time { font-size: 0.7rem; color: #64748b; display: flex; align-items: center; }
        .article-title { font-size: 0.9rem; color: #e2e8f0; display: flex; align-items: flex-start; gap: 0.3rem; line-height: 1.45; word-break: break-word; }
        .article-title:hover { color: #38bdf8; text-decoration: none; }
        .article-title.dim { color: #64748b; }
        .ext-icon { opacity: 0.4; flex-shrink: 0; margin-top: 3px; }
        .article-actions { display: flex; align-items: center; gap: 0.25rem; flex-shrink: 0; }
        .icon-btn { width: 28px; height: 28px; border-radius: 5px; border: none; background: #334155; color: #94a3b8; display: flex; align-items: center; justify-content: center; transition: all 0.15s; cursor: pointer; }
        .icon-btn:hover { background: #475569; color: #e2e8f0; }
        .icon-btn.active { background: #7f1d1d; color: #f87171; }
        .icon-btn.dup-mark:hover { background: #7f1d1d; color: #f87171; }
        .icon-btn.dup-restore { background: #1e3a1e; color: #4ade80; }
        .icon-btn.dup-restore:hover { background: #14532d; color: #86efac; }

        .translation-panel { margin-top: 0.25rem; }
        .trans-toggle { display: inline-flex; align-items: center; gap: 0.3rem; background: none; border: 1px solid #334155; border-radius: 4px; padding: 2px 8px; font-size: 0.72rem; color: #fb923c; cursor: pointer; transition: all 0.12s; }
        .trans-toggle:hover { border-color: #fb923c; background: #1c1006; }
        .trans-content { margin-top: 0.4rem; padding: 0.6rem 0.75rem; background: #0f172a; border-radius: 6px; border-left: 2px solid #fb923c; }
        .trans-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
        .trans-btn { padding: 2px 10px; border-radius: 4px; border: 1px solid #334155; background: #1e293b; color: #94a3b8; font-size: 0.75rem; cursor: pointer; display: flex; align-items: center; gap: 0.3rem; transition: all 0.12s; }
        .trans-btn:hover { border-color: #475569; color: #e2e8f0; }
        .trans-btn.active { border-color: #fb923c; color: #fb923c; background: #1c1006; }
        .trans-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .trans-meta { font-size: 0.7rem; color: #475569; margin-left: auto; }
        .trans-text { font-size: 0.85rem; color: #cbd5e1; line-height: 1.65; white-space: pre-wrap; }
        .trans-notice { font-size: 0.8rem; color: #64748b; }
        .trans-error { font-size: 0.8rem; color: #f87171; }

        .empty-state { display: flex; flex-direction: column; align-items: center; gap: 0.75rem; padding: 3rem 1rem; color: #475569; }
        .pagination { display: flex; align-items: center; justify-content: center; gap: 0.75rem; margin-top: 1rem; }
        .page-info { font-size: 0.875rem; color: #64748b; }
        .alert { padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; }
        .alert-success { background: #14532d; color: #4ade80; border: 1px solid #166534; }
        .spin { animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
